import { useCallback, useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { useParams } from 'react-router-dom';
import type {
  ArrayAggregation,
  AutomationLookupTriggerCondition,
  CiFieldExtraction,
  CiFieldExtractionRule,
  CiRelationFilterCondition,
  CiFieldSource,
  CiMatchingRule,
  EnrichmentConfig,
  EnrichmentRule,
  EnrichmentSourceType,
  FieldSuggestion,
  NormalizationConfig,
  RuleCondition,
  TraversalDirection,
} from '../../types/pipelineConfig';
import type {
  CiClassDto,
  ScriptDto,
  ScriptParameterDefinition,
  ScriptScope,
  ScriptSummaryDto,
  ServiceConnectionDto,
} from '../../types';
import { useCiClassesQuery } from '../../hooks/queries/useCiClassesQuery';
import { getDefaultEnrichmentConfig, getFieldSuggestions } from '../../utils/pipelineDefaults';
import { apiClient } from '../../services/api';
import { FieldPathInput } from './FieldPathInput';
import { StepConfigHeader } from './StepConfigHeader';
import { ConditionEditor } from './normalization/ConditionEditor';
import { getNormalizedTargetFieldOptions } from './normalization/normalizedTargetFields';

interface EnrichmentConfigEditorProps {
  config?: EnrichmentConfig;
  onChange: (config: EnrichmentConfig) => void;
  endpointType: string | number;
  tenantId?: string;
  customerId?: string;
  normalizationConfig?: NormalizationConfig | null;
  disabled?: boolean;
  isSystemDefault?: boolean;
  hideToggle?: boolean;
}

const LEGACY_SOURCE_OPTIONS: Array<{ value: EnrichmentSourceType; label: string }> = [
  { value: 'static', label: 'Static Value' },
  { value: 'lookup', label: 'Lookup Table' },
  { value: 'cmdb', label: 'CMDB Lookup' },
  { value: 'geoip', label: 'GeoIP Lookup' },
  { value: 'dns', label: 'DNS Lookup' },
  { value: 'custom', label: 'Custom Expression' },
];

type AvailableScript = ScriptSummaryDto &
  Partial<Pick<ScriptDto, 'scriptContent' | 'parameters' | 'systemKey'>>;

const SCRIPT_SCOPE_GROUP_LABELS: Record<ScriptScope, string> = {
  System: '🔒 System Scripts',
  Tenant: '🏢 Tenant Scripts',
  Customer: '👤 Customer Scripts',
};

function buildScriptParameterDefaults(
  parameters?: ScriptParameterDefinition[] | null,
): Record<string, string> {
  if (!parameters || parameters.length === 0) {
    return {};
  }

  return parameters.reduce<Record<string, string>>((acc, parameter) => {
    acc[parameter.name] = parameter.defaultValue ?? '';
    return acc;
  }, {});
}

const CI_MATCH_FIELD_OPTIONS: Array<{ value: CiMatchingRule['ciMatchField']; label: string }> = [
  { value: 'ExternalId', label: 'ExternalId' },
  { value: 'Name', label: 'Name' },
  { value: 'ResourceType', label: 'ResourceType' },
  { value: 'SubscriptionId', label: 'SubscriptionId' },
  { value: 'Region', label: 'Region' },
  { value: 'ResourceGroup', label: 'ResourceGroup' },
  { value: 'Tag', label: 'Tag' },
];

const MATCH_TYPE_OPTIONS: Array<{ value: CiMatchingRule['matchType']; label: string }> = [
  { value: 'exact', label: 'Exact' },
  { value: 'contains', label: 'Contains' },
  { value: 'regex', label: 'Regex' },
];

const RESULT_ACTION_OPTIONS: Array<{ value: CiMatchingRule['resultAction']; label: string }> = [
  { value: 'setAsPrimaryCi', label: 'Set as Primary CI' },
  { value: 'addAsAffectedCi', label: 'Add as Affected CI' },
  { value: 'addAsImpactedCi', label: 'Add as Impacted CI' },
  { value: 'setAsCausedByCi', label: 'Add as Caused By CI' },
  { value: 'addAsRelatedCi', label: 'Add as Related CI' },
];

const CONDITION_OPERATORS: Array<{ value: RuleCondition['operator']; label: string }> = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'regex', label: 'Matches Regex' },
  { value: 'exists', label: 'Exists' },
  { value: 'notExists', label: 'Not Exists' },
];

const RELATIONSHIP_TYPE_OPTIONS = [
  { value: 'has_component', label: 'Has Component' },
  { value: 'connected_to', label: 'Connected To' },
  { value: 'contains', label: 'Contains' },
  { value: 'protects', label: 'Protects' },
  { value: 'depends_on', label: 'Depends On' },
  { value: 'hosts', label: 'Hosts' },
  { value: 'runs_on', label: 'Runs On' },
  { value: 'connects_to', label: 'Connects To' },
  { value: 'part_of', label: 'Part Of' },
  { value: 'manages', label: 'Manages' },
  { value: 'backs_up', label: 'Backs Up' },
  { value: 'uses', label: 'Uses' },
];

const TRAVERSAL_DIRECTION_OPTIONS: Array<{ value: TraversalDirection; label: string }> = [
  { value: 'forward', label: 'Forward' },
  { value: 'reverse', label: 'Reverse' },
  { value: 'both', label: 'Both' },
];

const RELATION_FILTER_DIRECTION_OPTIONS: Array<{ value: CiRelationFilterCondition['direction']; label: string }> = [
  { value: 'Forward', label: 'Forward' },
  { value: 'Reverse', label: 'Reverse' },
  { value: 'Both', label: 'Both' },
];

const RELATION_FILTER_OPERATOR_OPTIONS: Array<{ value: CiRelationFilterCondition['operator']; label: string }> = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts With' },
];

const EXTRACTION_SCOPE_OPTIONS: Array<{ value: CiFieldSource; label: string }> = [
  { value: 'primary', label: 'Primary CI' },
  { value: 'affected', label: 'Affected CIs' },
  { value: 'impacted', label: 'Impacted CIs' },
  { value: 'caused', label: 'Caused By CI' },
  { value: 'all', label: 'All CIs' },
];

const EXTRACTION_AGGREGATION_OPTIONS: Array<{ value: ArrayAggregation; label: string }> = [
  { value: 'first', label: 'First' },
  { value: 'last', label: 'Last' },
  { value: 'join', label: 'Join' },
  { value: 'all', label: 'All' },
];

const EXTRACTION_FIELD_OPTIONS = [
  'Name',
  'Region',
  'ResourceGroup',
  'ResourceType',
  'SubscriptionId',
  'ExternalId',
  'CiClass',
] as const;

const CUSTOM_FIELD_SENTINEL = '__custom__';

function createId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

function reorderByIndex<T extends { priority: number }>(items: T[]): T[] {
  return items.map((item, index) => ({ ...item, priority: index + 1 }));
}

function getRuleResultActions(rule: CiMatchingRule): CiMatchingRule['resultAction'][] {
  if (rule.resultActions && rule.resultActions.length > 0) {
    return rule.resultActions;
  }

  return [rule.resultAction];
}

function migrateAutomationTrigger(trigger?: unknown): AutomationLookupTriggerCondition | undefined {
  if (!trigger || typeof trigger !== 'object') {
    return undefined;
  }

  const candidate = trigger as {
    field?: string;
    operator?: string;
    values?: string[];
    conditions?: AutomationLookupTriggerCondition['conditions'];
    conditionLogic?: AutomationLookupTriggerCondition['conditionLogic'];
  };

  if (candidate.conditions) {
    return {
      conditions: candidate.conditions,
      conditionLogic: candidate.conditionLogic ?? 'and',
    };
  }

  if (candidate.field && candidate.operator) {
    const migratedOperator =
      candidate.operator === 'ContainsAny'
        ? 'Contains'
        : candidate.operator === 'Matches'
          ? 'RegexMatch'
          : candidate.operator;

    return {
      conditions: [
        {
          field: candidate.field,
          operator: migratedOperator as AutomationLookupTriggerCondition['conditions'][number]['operator'],
          value: candidate.values?.join(', '),
        },
      ],
      conditionLogic: 'and',
    };
  }

  return undefined;
}

export function EnrichmentConfigEditor({
  config: rawConfig,
  onChange,
  endpointType,
  tenantId,
  customerId,
  normalizationConfig,
  disabled = false,
  isSystemDefault = false,
  hideToggle,
}: EnrichmentConfigEditorProps) {
  const defaultConfig = useMemo(() => getDefaultEnrichmentConfig(endpointType), [endpointType]);
  const config = useMemo<EnrichmentConfig>(() => ({
    ...defaultConfig,
    ...(rawConfig ?? {}),
    rules: rawConfig?.rules ?? defaultConfig.rules ?? [],
    ciMatchingRules: rawConfig?.ciMatchingRules ?? defaultConfig.ciMatchingRules ?? [],
    fieldExtractionRules: rawConfig?.fieldExtractionRules ?? defaultConfig.fieldExtractionRules ?? [],
    ciFieldExtractions: rawConfig?.ciFieldExtractions ?? defaultConfig.ciFieldExtractions ?? [],
    automationLookups: rawConfig?.automationLookups ?? defaultConfig.automationLookups ?? [],
    fallbackBehavior: rawConfig?.fallbackBehavior ?? defaultConfig.fallbackBehavior,
  }), [defaultConfig, rawConfig]);
  const isStepEnabled = (config as EnrichmentConfig & { isEnabled?: boolean }).isEnabled ?? true;
  const { tenantId: routeTenantId, customerId: routeCustomerId } = useParams<{
    tenantId?: string;
    customerId?: string;
  }>();
  const effectiveTenantId = isSystemDefault ? undefined : (tenantId || routeTenantId);
  const effectiveCustomerId = isSystemDefault ? null : (customerId ?? routeCustomerId ?? null);
  const ciFieldExtractions = useMemo(() => config.ciFieldExtractions ?? [], [config.ciFieldExtractions]);
  const suggestions = useMemo<FieldSuggestion[]>(() => {
    const baseSuggestions = getFieldSuggestions(endpointType, 'enrichment');
    const normalizedSuggestions = getNormalizedTargetFieldOptions(normalizationConfig).map((option) => ({
      path: `normalized.${option.value}`,
      label: `Normalized ${option.label}`,
      description: 'Derived from normalization configuration',
      category: 'Normalized Fields',
    }));
    const computedSuggestions: FieldSuggestion[] = [
      {
        path: 'computed.subscriptionId',
        label: 'Subscription ID',
        description: 'Extracted from Azure Resource ID',
        category: 'Computed Fields',
      },
      {
        path: 'computed.resourceGroup',
        label: 'Resource Group',
        description: 'Extracted from Azure Resource ID',
        category: 'Computed Fields',
      },
      {
        path: 'computed.resourceName',
        label: 'Resource Name',
        description: 'Last segment of Azure Resource ID',
        category: 'Computed Fields',
      },
      {
        path: 'computed.resourceType',
        label: 'Resource Type',
        description: 'Provider/Type from Azure Resource ID',
        category: 'Computed Fields',
      },
      {
        path: 'computed.provider',
        label: 'Provider',
        description: 'Provider name from Azure Resource ID',
        category: 'Computed Fields',
      },
    ];
    const ciSuggestions: FieldSuggestion[] = [
      {
        path: 'ci.osType',
        label: 'CI OS Type',
        description: 'Extracted from matched CI ResourceJson',
        category: 'ci.* Fields',
      },
      ...ciFieldExtractions
        .filter((extraction) => extraction.targetField.trim().length > 0)
        .map((extraction) => ({
          path: extraction.targetField,
          label: extraction.targetField,
          description: extraction.sourcePath
            ? `Extracted from CI path ${extraction.sourcePath}`
            : 'Extracted from matched CI ResourceJson',
          category: 'ci.* Fields',
        })),
    ];

    const uniqueSuggestions = new Map<string, FieldSuggestion>();
    [...baseSuggestions, ...normalizedSuggestions, ...computedSuggestions, ...ciSuggestions].forEach(
      (suggestion) => {
        if (!uniqueSuggestions.has(suggestion.path)) {
          uniqueSuggestions.set(suggestion.path, suggestion);
        }
      },
    );

    return [...uniqueSuggestions.values()];
  }, [ciFieldExtractions, endpointType, normalizationConfig]);

  const ciMatchingRules = useMemo(() => config.ciMatchingRules ?? [], [config.ciMatchingRules]);
  const [collapsedRules, setCollapsedRules] = useState<Set<string>>(
    () => new Set(config.ciMatchingRules?.map((r) => r.id) ?? []),
  );
  const [addingParameterForLookup, setAddingParameterForLookup] = useState<number | null>(null);
  const [newParameterName, setNewParameterName] = useState('');
  const [newParameterValue, setNewParameterValue] = useState('');
  const [addingMappingForLookup, setAddingMappingForLookup] = useState<number | null>(null);
  const [newMappingTarget, setNewMappingTarget] = useState('');
  const [newMappingSource, setNewMappingSource] = useState('');
  const [scriptModalScript, setScriptModalScript] = useState<AvailableScript | null>(null);
  const fieldExtractionRules = useMemo(
    () => config.fieldExtractionRules ?? [],
    [config.fieldExtractionRules],
  );
  const { data: ciClasses = [], isLoading: isLoadingCiClasses } = useCiClassesQuery(effectiveTenantId, {
    enabled: !isSystemDefault && !!effectiveTenantId && config.enableCmdbEnrichment,
  });
  const ciClassOptions = useMemo(() => {
    const classesById = new Map(ciClasses.map((c) => [c.id, c]));
    const buildLabel = (c: CiClassDto): string => {
      const segs: string[] = [];
      const vis = new Set<string>();
      let cur: CiClassDto | undefined = c;
      while (cur && !vis.has(cur.id)) {
        vis.add(cur.id);
        segs.unshift(cur.name);
        cur = cur.parentClassId ? classesById.get(cur.parentClassId) : undefined;
      }
      return segs.join(' > ');
    };
    return [...ciClasses]
      .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))
      .map((c) => ({ id: c.id, icon: c.icon, label: buildLabel(c) }));
  }, [ciClasses]);
  const fallbackBehavior = useMemo(
    () => config.fallbackBehavior ?? { whenNoPrimaryCiFound: 'continue' as const },
    [config.fallbackBehavior],
  );
  const {
    data: availableScripts = [],
    isLoading: isLoadingScripts,
    error: scriptsError,
  } = useQuery({
    queryKey: ['scripts', effectiveTenantId, effectiveCustomerId],
    queryFn: async () => {
      const scripts = await apiClient.getScripts(effectiveTenantId!, effectiveCustomerId ?? undefined);
      return scripts as AvailableScript[];
    },
    enabled: !isSystemDefault && Boolean(effectiveTenantId),
    staleTime: 60_000,
  });
  const activeScripts = useMemo(
    () => availableScripts.filter((script) => script.isActive),
    [availableScripts],
  );
  const scriptsById = useMemo(
    () => new Map(activeScripts.map((script) => [script.id, script])),
    [activeScripts],
  );
  const scriptsBySystemKey = useMemo(
    () =>
      new Map(
        activeScripts
          .filter((script) => !!script.systemKey)
          .map((script) => [script.systemKey as string, script]),
      ),
    [activeScripts],
  );
  const scriptsByScope = useMemo(
    () => ({
      System: activeScripts.filter((script) => script.scope === 'System'),
      Tenant: activeScripts.filter((script) => script.scope === 'Tenant'),
      Customer: activeScripts.filter((script) => script.scope === 'Customer'),
    }),
    [activeScripts],
  );
  const { data: serviceConnectionsResponse, isLoading: isLoadingServiceConnections } = useQuery({
    queryKey: ['service-connections', effectiveTenantId, effectiveCustomerId],
    queryFn: () => apiClient.getServiceConnections(effectiveTenantId!, effectiveCustomerId!),
    enabled: !isSystemDefault && Boolean(effectiveTenantId && effectiveCustomerId),
    staleTime: 60_000,
  });
  const serviceConnections = (serviceConnectionsResponse?.items ?? []) as ServiceConnectionDto[];

  const updateConfig = useCallback(
    (patch: Partial<EnrichmentConfig>) => {
      onChange({ ...config, ...patch });
    },
    [config, onChange],
  );

  const updateMatchingRule = useCallback(
    (id: string, patch: Partial<CiMatchingRule>) => {
      updateConfig({
        ciMatchingRules: ciMatchingRules.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)),
      });
    },
    [ciMatchingRules, updateConfig],
  );

  const addMatchingRule = useCallback(() => {
    updateConfig({
      ciMatchingRules: [
        ...ciMatchingRules,
        {
          id: createId('ci-match'),
          name: 'New CI matching rule',
          enabled: true,
          priority: ciMatchingRules.length + 1,
          sourceField: '',
          sourceIsArray: false,
          ciMatchField: 'ExternalId',
          matchType: 'exact',
          resultAction: 'setAsPrimaryCi',
          resultActions: ['setAsPrimaryCi'],
          conditions: [],
          conditionLogic: 'all',
          ciClassIds: [],
          includeDescendantClasses: false,
          relationTraversalRules: [],
        },
      ],
    });
  }, [ciMatchingRules, updateConfig]);

  const removeMatchingRule = useCallback(
    (id: string) => {
      updateConfig({
        ciMatchingRules: reorderByIndex(ciMatchingRules.filter((rule) => rule.id !== id)),
      });
    },
    [ciMatchingRules, updateConfig],
  );

  const moveMatchingRule = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= ciMatchingRules.length) return;

      const reordered = [...ciMatchingRules];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      updateConfig({ ciMatchingRules: reorderByIndex(reordered) });
    },
    [ciMatchingRules, updateConfig],
  );

  const toggleRuleCollapsed = (ruleId: string) => {
    setCollapsedRules((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  };

  const addRelationFilterCondition = useCallback(
    (ruleId: string) => {
      const rule = ciMatchingRules.find((item) => item.id === ruleId);
      if (!rule) {
        return;
      }

      updateMatchingRule(ruleId, {
        relationFilterConditions: [
          ...(rule.relationFilterConditions ?? []),
          {
            id: createId('relation-filter'),
            relationshipTypeCode: '',
            direction: 'Both',
            relatedCiMatchField: 'Name',
            relatedCiMatchValue: '',
            operator: 'equals',
          },
        ],
      });
    },
    [ciMatchingRules, updateMatchingRule],
  );

  const updateRelationFilter = useCallback(
    (ruleId: string, conditionId: string, patch: Partial<CiRelationFilterCondition>) => {
      const rule = ciMatchingRules.find((item) => item.id === ruleId);
      if (!rule) {
        return;
      }

      updateMatchingRule(ruleId, {
        relationFilterConditions: (rule.relationFilterConditions ?? []).map((condition) =>
          condition.id === conditionId ? { ...condition, ...patch } : condition,
        ),
      });
    },
    [ciMatchingRules, updateMatchingRule],
  );

  const removeRelationFilter = useCallback(
    (ruleId: string, conditionId: string) => {
      const rule = ciMatchingRules.find((item) => item.id === ruleId);
      if (!rule) {
        return;
      }

      updateMatchingRule(ruleId, {
        relationFilterConditions: (rule.relationFilterConditions ?? []).filter(
          (condition) => condition.id !== conditionId,
        ),
      });
    },
    [ciMatchingRules, updateMatchingRule],
  );

  const addCondition = useCallback(
    (ruleId: string) => {
      const rule = ciMatchingRules.find((item) => item.id === ruleId);
      if (!rule) {
        return;
      }

      updateMatchingRule(ruleId, {
        conditions: [
          ...(rule.conditions ?? []),
          {
            id: crypto.randomUUID().substring(0, 8),
            field: '',
            operator: 'equals',
            value: '',
            caseSensitive: false,
          },
        ],
        conditionLogic: rule.conditionLogic ?? 'all',
      });
    },
    [ciMatchingRules, updateMatchingRule],
  );

  const updateCondition = useCallback(
    (ruleId: string, conditionId: string, patch: Partial<RuleCondition>) => {
      const rule = ciMatchingRules.find((item) => item.id === ruleId);
      if (!rule) {
        return;
      }

      updateMatchingRule(ruleId, {
        conditions: (rule.conditions ?? []).map((condition) =>
          condition.id === conditionId ? { ...condition, ...patch } : condition,
        ),
      });
    },
    [ciMatchingRules, updateMatchingRule],
  );

  const removeCondition = useCallback(
    (ruleId: string, conditionId: string) => {
      const rule = ciMatchingRules.find((item) => item.id === ruleId);
      if (!rule) {
        return;
      }

      updateMatchingRule(ruleId, {
        conditions: (rule.conditions ?? []).filter((condition) => condition.id !== conditionId),
      });
    },
    [ciMatchingRules, updateMatchingRule],
  );

  const updateExtractionRule = useCallback(
    (id: string, patch: Partial<CiFieldExtractionRule>) => {
      updateConfig({
        fieldExtractionRules: fieldExtractionRules.map((rule) =>
          rule.id === id ? { ...rule, ...patch } : rule,
        ),
      });
    },
    [fieldExtractionRules, updateConfig],
  );

  const addExtractionRule = useCallback(() => {
    updateConfig({
      fieldExtractionRules: [
        ...fieldExtractionRules,
        {
          id: createId('ci-extract'),
          enabled: true,
          sourceCiScope: 'primary',
          ciField: 'Name',
          variableName: 'ci.name',
          arrayAggregation: 'first',
        },
      ],
    });
  }, [fieldExtractionRules, updateConfig]);

  const removeExtractionRule = useCallback(
    (id: string) => {
      updateConfig({
        fieldExtractionRules: fieldExtractionRules.filter((rule) => rule.id !== id),
      });
    },
    [fieldExtractionRules, updateConfig],
  );

  const updateCiFieldExtraction = useCallback(
    (index: number, patch: Partial<CiFieldExtraction>) => {
      updateConfig({
        ciFieldExtractions: ciFieldExtractions.map((extraction, extractionIndex) =>
          extractionIndex === index ? { ...extraction, ...patch } : extraction,
        ),
      });
    },
    [ciFieldExtractions, updateConfig],
  );

  const addCiFieldExtraction = useCallback(() => {
    updateConfig({
      ciFieldExtractions: [
        ...ciFieldExtractions,
        {
          sourcePath: '',
          targetField: 'ci.',
        },
      ],
    });
  }, [ciFieldExtractions, updateConfig]);

  const removeCiFieldExtraction = useCallback(
    (index: number) => {
      updateConfig({
        ciFieldExtractions: ciFieldExtractions.filter((_, extractionIndex) => extractionIndex !== index),
      });
    },
    [ciFieldExtractions, updateConfig],
  );

  const updateLegacyRule = useCallback(
    (id: string, patch: Partial<EnrichmentRule>) => {
      updateConfig({
        rules: config.rules.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)),
      });
    },
    [config.rules, updateConfig],
  );

  const addLegacyRule = useCallback(() => {
    updateConfig({
      rules: [
        ...config.rules,
        {
          id: createId('enrich'),
          name: 'New enrichment rule',
          sourceType: 'static',
          enabled: true,
          priority: config.rules.length + 1,
          targetField: '',
          staticValue: '',
          timeoutMs: config.defaultTimeoutMs,
        },
      ],
    });
  }, [config.defaultTimeoutMs, config.rules, updateConfig]);

  const removeLegacyRule = useCallback(
    (id: string) => {
      updateConfig({
        rules: reorderByIndex(config.rules.filter((rule) => rule.id !== id)),
      });
    },
    [config.rules, updateConfig],
  );

  const moveLegacyRule = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= config.rules.length) return;

      const reordered = [...config.rules];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      updateConfig({ rules: reorderByIndex(reordered) });
    },
    [config.rules, updateConfig],
  );

  const globalSettingsCount = [
    config.enableCmdbEnrichment ? 'CMDB enabled' : null,
    config.failurePolicy !== 'skip' ? `Policy: ${config.failurePolicy}` : null,
    fallbackBehavior.whenNoPrimaryCiFound !== 'continue' ? `Fallback: ${fallbackBehavior.whenNoPrimaryCiFound}` : null,
  ].filter(Boolean).length || 2;

  return (
    <div className="space-y-4">
      <StepConfigHeader
        title="Enrichment"
        isEnabled={isStepEnabled}
        onToggleEnabled={(enabled) =>
          onChange({ ...config, isEnabled: enabled } as EnrichmentConfig & { isEnabled: boolean })
        }
        isSystemDefault={isSystemDefault}
        disabled={disabled}
        hideToggle={hideToggle}
      />
      <div className={isStepEnabled ? 'space-y-4' : 'space-y-4 opacity-50 pointer-events-none'}>
      {/* 1. Global Settings (includes fallback behavior) */}
      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <summary className="cursor-pointer px-5 py-4 flex items-center gap-3 select-none list-none">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">⚙️ Global Settings</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
            {globalSettingsCount} configured
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto mr-2">
            Pipeline-wide configuration and fallback behavior
          </span>
        </summary>
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 dark:border-gray-800 pt-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.enableCmdbEnrichment}
              onChange={(event) => updateConfig({ enableCmdbEnrichment: event.target.checked })}
              disabled={disabled}
            />
            Enable CMDB Enrichment
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Failure Policy</label>
              <select
                className="input"
                value={config.failurePolicy}
                onChange={(event) =>
                  updateConfig({ failurePolicy: event.target.value as EnrichmentConfig['failurePolicy'] })
                }
                disabled={disabled}
              >
                <option value="skip">Skip rule on failure</option>
                <option value="fail">Fail step on failure</option>
                <option value="default">Use default values</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Timeout (ms)</label>
              <input
                type="number"
                min={100}
                className="input"
                value={config.defaultTimeoutMs}
                onChange={(event) => updateConfig({ defaultTimeoutMs: Number(event.target.value) || 100 })}
                disabled={disabled}
              />
            </div>
          </div>

          <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Fallback Behavior</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">When no Primary CI found</label>
                <select
                  className="input"
                  value={fallbackBehavior.whenNoPrimaryCiFound}
                  onChange={(event) =>
                    updateConfig({
                      fallbackBehavior: {
                        ...fallbackBehavior,
                        whenNoPrimaryCiFound: event.target.value as 'continue' | 'fail' | 'useDefault',
                        defaultPrimaryCiId:
                          event.target.value === 'useDefault'
                            ? fallbackBehavior.defaultPrimaryCiId ?? ''
                            : undefined,
                      },
                    })
                  }
                  disabled={disabled}
                >
                  <option value="continue">Continue</option>
                  <option value="fail">Fail</option>
                  <option value="useDefault">Use Default</option>
                </select>
              </div>
              {fallbackBehavior.whenNoPrimaryCiFound === 'useDefault' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Primary CI ID</label>
                  <input
                    className="input"
                    value={fallbackBehavior.defaultPrimaryCiId ?? ''}
                    onChange={(event) =>
                      updateConfig({
                        fallbackBehavior: {
                          ...fallbackBehavior,
                          defaultPrimaryCiId: event.target.value,
                        },
                      })
                    }
                    disabled={disabled}
                    placeholder="CI-12345"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </details>

      {/* 2. CI Matching Rules */}
      {config.enableCmdbEnrichment && (
        <details className="rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-gray-900">
          <summary className="cursor-pointer px-5 py-4 flex items-center gap-3 select-none list-none">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">🔍 CI Matching Rules</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
              {ciMatchingRules.length} {ciMatchingRules.length === 1 ? 'rule' : 'rules'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto mr-2">
              How to find Configuration Items from event payload
            </span>
          </summary>
          <div className="px-5 pb-5 space-y-4 border-t border-purple-100 dark:border-purple-800 pt-4">
            <div className="flex justify-end">
              <button
                type="button"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                onClick={() => {
                  const allIds = ciMatchingRules.map((r) => r.id);
                  setCollapsedRules((prev) => (prev.size === allIds.length ? new Set() : new Set(allIds)));
                }}
              >
                {collapsedRules.size === ciMatchingRules.length ? 'Expand All' : 'Collapse All'}
              </button>
            </div>

          <div className="space-y-4">
            {ciMatchingRules.map((rule, index) => (
              <div key={rule.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => toggleRuleCollapsed(rule.id)}>
                  <span className="text-xs text-gray-400">{collapsedRules.has(rule.id) ? '▶' : '▼'}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</span>
                  <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={(event) => updateMatchingRule(rule.id, { enabled: event.target.checked })}
                      disabled={disabled}
                    />
                    Enabled
                  </label>
                  {collapsedRules.has(rule.id) && (
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate ml-2">
                      <strong>{rule.name || '(unnamed)'}</strong>
                      <span className="text-gray-400 mx-2">·</span>
                      <span className="text-gray-500">{rule.sourceField || '(no source)'}</span>
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
                      onClick={() => moveMatchingRule(index, 'up')}
                      disabled={disabled || index === 0}
                      aria-label="Move matching rule up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
                      onClick={() => moveMatchingRule(index, 'down')}
                      disabled={disabled || index === ciMatchingRules.length - 1}
                      aria-label="Move matching rule down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                      onClick={() => removeMatchingRule(rule.id)}
                      disabled={disabled}
                      aria-label="Delete matching rule"
                    >
                      🗑
                    </button>
                  </div>
                </div>

                {!collapsedRules.has(rule.id) && (
                  <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                    <input
                      className="input"
                      value={rule.name}
                      onChange={(event) => updateMatchingRule(rule.id, { name: event.target.value })}
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Field</label>
                    <FieldPathInput
                      value={rule.sourceField}
                      onChange={(sourceField) => updateMatchingRule(rule.id, { sourceField })}
                      suggestions={suggestions}
                      disabled={disabled}
                      placeholder="e.g., data.essentials.alertTargetIDs"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Field path from normalized payload or raw event, e.g., data.essentials.alertTargetIDs
                    </p>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={rule.sourceIsArray}
                    onChange={(event) => updateMatchingRule(rule.id, { sourceIsArray: event.target.checked })}
                    disabled={disabled}
                  />
                  Check if the source field contains an array of values
                </label>
                  {rule.sourceIsArray && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 ml-4">
                    <input
                      type="checkbox"
                      checked={rule.skipFirstArrayElement ?? false}
                      onChange={(e) => updateMatchingRule(rule.id, { skipFirstArrayElement: e.target.checked })}
                      disabled={disabled}
                    />
                    Skip first array element (use when first element is handled by another rule)
                  </label>
                )}

                <details className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <summary className="cursor-pointer list-none flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">⚙️ Conditional Rule Execution</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {(rule.conditions ?? []).length} condition{(rule.conditions ?? []).length === 1 ? '' : 's'}
                    </span>
                  </summary>

                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Condition Logic</label>
                        <select
                          className="input"
                          value={rule.conditionLogic ?? 'all'}
                          onChange={(event) =>
                            updateMatchingRule(rule.id, { conditionLogic: event.target.value as CiMatchingRule['conditionLogic'] })
                          }
                          disabled={disabled}
                          aria-label="Condition logic"
                        >
                          <option value="all">ALL conditions must match (AND)</option>
                          <option value="any">ANY condition can match (OR)</option>
                        </select>
                      </div>
                      <div className="md:text-right">
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md bg-primary-700 hover:bg-primary-800 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
                          onClick={() => addCondition(rule.id)}
                          disabled={disabled}
                          aria-label={`Add condition to ${rule.name || 'matching rule'}`}
                        >
                          Add Condition
                        </button>
                      </div>
                    </div>

                    {(rule.conditions ?? []).length === 0 ? (
                      <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-3 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between gap-3">
                        <span>No conditions — rule always executes.</span>
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md bg-primary-700 hover:bg-primary-800 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                          onClick={() => addCondition(rule.id)}
                          disabled={disabled}
                          aria-label={`Add first condition to ${rule.name || 'matching rule'}`}
                        >
                          Add Condition
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(rule.conditions ?? []).map((condition, conditionIndex) => {
                          const hideValueInput =
                            condition.operator === 'exists' || condition.operator === 'notExists';

                          return (
                            <div
                              key={condition.id}
                              className="rounded-lg border border-gray-200 dark:border-gray-600 p-3 space-y-2"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                  Condition {conditionIndex + 1}
                                </span>
                                <button
                                  type="button"
                                  className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                                  onClick={() => removeCondition(rule.id, condition.id)}
                                  disabled={disabled}
                                  aria-label={`Remove condition ${conditionIndex + 1}`}
                                >
                                  ✕
                                </button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                                <div className={hideValueInput ? 'md:col-span-5' : 'md:col-span-4'}>
                                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Field</label>
                                  <input
                                    className="input text-xs"
                                    value={condition.field}
                                    onChange={(event) =>
                                      updateCondition(rule.id, condition.id, { field: event.target.value })
                                    }
                                    disabled={disabled}
                                    placeholder="e.g., title"
                                    aria-label={`Condition ${conditionIndex + 1} field`}
                                  />
                                </div>

                                <div className={hideValueInput ? 'md:col-span-5' : 'md:col-span-3'}>
                                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Operator</label>
                                  <select
                                    className="input text-xs"
                                    value={condition.operator}
                                    onChange={(event) => {
                                      const nextOperator = event.target.value as RuleCondition['operator'];
                                      updateCondition(rule.id, condition.id, {
                                        operator: nextOperator,
                                        value: nextOperator === 'exists' || nextOperator === 'notExists' ? undefined : (condition.value ?? ''),
                                      });
                                    }}
                                    disabled={disabled}
                                    aria-label={`Condition ${conditionIndex + 1} operator`}
                                  >
                                    {CONDITION_OPERATORS.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {!hideValueInput && (
                                  <div className="md:col-span-3">
                                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Value</label>
                                    <input
                                      className="input text-xs"
                                      value={condition.value ?? ''}
                                      onChange={(event) =>
                                        updateCondition(rule.id, condition.id, { value: event.target.value })
                                      }
                                      disabled={disabled}
                                      placeholder="Value"
                                      aria-label={`Condition ${conditionIndex + 1} value`}
                                    />
                                  </div>
                                )}

                                <div className="md:col-span-2">
                                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 h-10">
                                    <input
                                      type="checkbox"
                                      checked={condition.caseSensitive}
                                      onChange={(event) =>
                                        updateCondition(rule.id, condition.id, { caseSensitive: event.target.checked })
                                      }
                                      disabled={disabled}
                                      aria-label={`Condition ${conditionIndex + 1} case sensitive`}
                                    />
                                    Case Sensitive
                                  </label>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </details>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Match CI Field</label>
                    <select
                      className="input"
                      value={rule.ciMatchField}
                      onChange={(event) =>
                        updateMatchingRule(rule.id, {
                          ciMatchField: event.target.value as CiMatchingRule['ciMatchField'],
                          ciMatchTagKey: event.target.value === 'Tag' ? rule.ciMatchTagKey ?? '' : undefined,
                        })
                      }
                      disabled={disabled}
                    >
                      {CI_MATCH_FIELD_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {rule.ciMatchField === 'Tag' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tag Key</label>
                      <input
                        className="input"
                        value={rule.ciMatchTagKey ?? ''}
                        onChange={(event) => updateMatchingRule(rule.id, { ciMatchTagKey: event.target.value })}
                        disabled={disabled}
                        placeholder="e.g., environment"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Match Type</label>
                    <select
                      className="input"
                      value={rule.matchType}
                      onChange={(event) =>
                        updateMatchingRule(rule.id, { matchType: event.target.value as CiMatchingRule['matchType'] })
                      }
                      disabled={disabled}
                    >
                      {MATCH_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">CI Class Filter</label>
                  {isSystemDefault ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      CI class filters are tenant-specific. Configure them per endpoint pipeline.
                    </p>
                  ) : !effectiveTenantId ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">CI class filtering available when tenant context is selected.</p>
                  ) : (
                    <>
                      <Autocomplete
                        multiple
                        size="small"
                        options={ciClassOptions}
                        value={ciClassOptions.filter(c => (rule.ciClassIds ?? []).includes(c.id))}
                        onChange={(_, values) => updateMatchingRule(rule.id, { ciClassIds: values.map(v => v.id) })}
                        getOptionLabel={(option) => `${option.icon ? option.icon + ' ' : ''}${option.label}`}
                        filterSelectedOptions
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        disabled={disabled || isLoadingCiClasses}
                        renderInput={(params) => (
                          <TextField {...params} size="small" placeholder="Search CI classes..." label="CI Class Filter" />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.id}
                              size="small"
                              label={`${option.icon ? option.icon + ' ' : ''}${option.label}`}
                              onDelete={disabled ? undefined : getTagProps({ index }).onDelete}
                            />
                          ))
                        }
                      />
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={rule.includeDescendantClasses ?? false}
                          onChange={(e) => updateMatchingRule(rule.id, { includeDescendantClasses: e.target.checked })}
                          disabled={disabled}
                        />{' '}
                        Include descendant classes
                      </label>
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">🔍 Relation Filter Conditions</label>
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      onClick={() => addRelationFilterCondition(rule.id)}
                      disabled={disabled}
                    >
                      + Add Condition
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    All conditions must match for a candidate CI to remain. You can use placeholders like {'{resourceGroup}'} in Related Match Value.
                  </p>
                  {(rule.relationFilterConditions ?? []).length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">No relation filter conditions configured.</p>
                  )}
                  {(rule.relationFilterConditions ?? []).map((condition, conditionIndex) => (
                    <div
                      key={condition.id}
                      className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Condition {conditionIndex + 1}</span>
                        <button
                          type="button"
                          className="p-1 rounded text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                          onClick={() => removeRelationFilter(rule.id, condition.id)}
                          disabled={disabled}
                          aria-label={`Remove relation filter condition ${conditionIndex + 1}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Relationship Type</label>
                          <select
                            className="input text-xs"
                            value={condition.relationshipTypeCode}
                            onChange={(event) =>
                              updateRelationFilter(rule.id, condition.id, { relationshipTypeCode: event.target.value })
                            }
                            disabled={disabled}
                          >
                            <option value="">Any</option>
                            {RELATIONSHIP_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Direction</label>
                          <select
                            className="input text-xs"
                            value={condition.direction}
                            onChange={(event) =>
                              updateRelationFilter(rule.id, condition.id, {
                                direction: event.target.value as CiRelationFilterCondition['direction'],
                              })
                            }
                            disabled={disabled}
                          >
                            {RELATION_FILTER_DIRECTION_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Related CI Field</label>
                          <select
                            className="input text-xs"
                            value={condition.relatedCiMatchField}
                            onChange={(event) =>
                              updateRelationFilter(rule.id, condition.id, {
                                relatedCiMatchField: event.target.value as CiRelationFilterCondition['relatedCiMatchField'],
                              })
                            }
                            disabled={disabled}
                          >
                            {CI_MATCH_FIELD_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Operator</label>
                          <select
                            className="input text-xs"
                            value={condition.operator}
                            onChange={(event) =>
                              updateRelationFilter(rule.id, condition.id, {
                                operator: event.target.value as CiRelationFilterCondition['operator'],
                              })
                            }
                            disabled={disabled}
                          >
                            {RELATION_FILTER_OPERATOR_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Related Match Value</label>
                          <input
                            className="input text-xs"
                            value={condition.relatedCiMatchValue}
                            onChange={(event) =>
                              updateRelationFilter(rule.id, condition.id, { relatedCiMatchValue: event.target.value })
                            }
                            disabled={disabled}
                            placeholder="e.g., {resourceGroup}"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Result Actions</label>
                  <select
                    className="input"
                    value={rule.resultAction}
                    onChange={(event) =>
                      updateMatchingRule(rule.id, {
                        resultAction: event.target.value as CiMatchingRule['resultAction'],
                        resultActions: [event.target.value as CiMatchingRule['resultAction']],
                      })
                    }
                    disabled={disabled}
                  >
                    {RESULT_ACTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {RESULT_ACTION_OPTIONS.map((option) => {
                      const selectedActions = getRuleResultActions(rule);
                      const checked = selectedActions.includes(option.value);
                      return (
                        <label key={`${rule.id}-${option.value}`} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => {
                              const currentActions = getRuleResultActions(rule);
                              const nextActions = event.target.checked
                                ? [...currentActions, option.value]
                                : currentActions.filter((item) => item !== option.value);

                              if (nextActions.length === 0) {
                                updateMatchingRule(rule.id, {
                                  resultAction: rule.resultAction,
                                  resultActions: undefined,
                                });
                                return;
                              }

                              updateMatchingRule(rule.id, {
                                resultAction: nextActions[0],
                                resultActions: Array.from(new Set(nextActions)),
                              });
                            }}
                            disabled={disabled}
                          />
                          {option.label}
                        </label>
                      );
                    })}
                  </div>
                  {rule.resultAction === 'setAsPrimaryCi' && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      First-match-wins: only the first CI matched by enabled rules (in priority order)
                      is used for this relationship.
                    </p>
                  )}
                  {rule.resultAction === 'addAsImpactedCi' && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Multi-CI behavior: all matched CIs are added as impacted CIs (deduplicated by CI ID).
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">🔗 Relationship Traversal</label>
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      onClick={() =>
                        updateMatchingRule(rule.id, {
                          relationTraversalRules: [
                            ...(rule.relationTraversalRules ?? []),
                            {
                              relationshipTypeCode: 'runs_on',
                              direction: 'forward' as TraversalDirection,
                              maxDepth: 1,
                              resultAction: 'addAsRelatedCi' as CiMatchingRule['resultAction'],
                            },
                          ],
                        })
                      }
                      disabled={disabled}
                    >
                      + Add Traversal Rule
                    </button>
                  </div>
                  {(rule.relationTraversalRules ?? []).length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">No traversal rules. Add rules to discover related CIs after matching.</p>
                  )}
                  {(rule.relationTraversalRules ?? []).map((tRule, tIdx) => (
                    <div key={`${rule.id}-trav-${tIdx}`} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Traversal Rule {tIdx + 1}</span>
                        <button
                          type="button"
                          className="p-1 rounded text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                          onClick={() =>
                            updateMatchingRule(rule.id, {
                              relationTraversalRules: (rule.relationTraversalRules ?? []).filter((_, i) => i !== tIdx),
                            })
                          }
                          disabled={disabled}
                          aria-label={`Remove traversal rule ${tIdx + 1}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Relationship Type</label>
                          <select
                            className="input text-xs"
                            value={tRule.relationshipTypeCode}
                            onChange={(e) => {
                              const u = [...(rule.relationTraversalRules ?? [])];
                              u[tIdx] = { ...tRule, relationshipTypeCode: e.target.value };
                              updateMatchingRule(rule.id, { relationTraversalRules: u });
                            }}
                            disabled={disabled}
                          >
                            {RELATIONSHIP_TYPE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Direction</label>
                          <select
                            className="input text-xs"
                            value={tRule.direction}
                            onChange={(e) => {
                              const u = [...(rule.relationTraversalRules ?? [])];
                              u[tIdx] = { ...tRule, direction: e.target.value as TraversalDirection };
                              updateMatchingRule(rule.id, { relationTraversalRules: u });
                            }}
                            disabled={disabled}
                          >
                            {TRAVERSAL_DIRECTION_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Max Depth</label>
                          <input
                            type="number"
                            className="input text-xs"
                            min={1}
                            max={3}
                            value={tRule.maxDepth}
                            onChange={(e) => {
                              const u = [...(rule.relationTraversalRules ?? [])];
                              u[tIdx] = { ...tRule, maxDepth: Math.min(3, Math.max(1, parseInt(e.target.value, 10) || 1)) };
                              updateMatchingRule(rule.id, { relationTraversalRules: u });
                            }}
                            disabled={disabled}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Result Action</label>
                          <select
                            className="input text-xs"
                            value={tRule.resultAction}
                            onChange={(e) => {
                              const u = [...(rule.relationTraversalRules ?? [])];
                              u[tIdx] = { ...tRule, resultAction: e.target.value as CiMatchingRule['resultAction'] };
                              updateMatchingRule(rule.id, { relationTraversalRules: u });
                            }}
                            disabled={disabled}
                          >
                            {RESULT_ACTION_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300 hover:border-blue-400"
            onClick={addMatchingRule}
            disabled={disabled}
          >
            + Add Matching Rule
          </button>
        </div>
      </details>
      )}

      {/* 3. CI Field Extractions - MERGED SECTION */}
      <details className="rounded-lg border border-teal-200 dark:border-teal-700 bg-white dark:bg-gray-900">
        <summary className="cursor-pointer px-5 py-4 flex items-center gap-3 select-none list-none">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">📋 CI Field Extractions</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
            {(fieldExtractionRules.length + ciFieldExtractions.length)} {((fieldExtractionRules.length + ciFieldExtractions.length) === 1 ? 'extraction' : 'extractions')}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto mr-2">
            Extract fields from matched CIs and event payload
          </span>
        </summary>
        <div className="px-5 pb-5 space-y-6 border-t border-teal-100 dark:border-teal-800 pt-4">
          {/* From CI ResourceJson subsection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-200">From CI ResourceJson</h5>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {ciFieldExtractions.length} {ciFieldExtractions.length === 1 ? 'extraction' : 'extractions'}
              </span>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 px-3 py-2 text-sm text-blue-800 dark:text-blue-200">
              Extract values from the matched CI ResourceJson and expose them as pipeline fields such as <code className="font-mono">ci.osType</code>.
            </div>

            <div className="space-y-3">
              {ciFieldExtractions.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No CI ResourceJson extractions configured yet.</p>
              ) : null}
              {ciFieldExtractions.map((extraction, index) => (
                <div
                  key={`${extraction.targetField || 'ci-field-extraction'}-${index}`}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
                >
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] gap-3 items-center">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Source Path</label>
                      <input
                        className="input font-mono text-sm"
                        value={extraction.sourcePath}
                        onChange={(event) =>
                          updateCiFieldExtraction(index, { sourcePath: event.target.value })
                        }
                        disabled={disabled}
                        placeholder="properties.storageProfile.osDisk.osType"
                      />
                    </div>
                    <div className="hidden md:flex items-center justify-center text-gray-400 dark:text-gray-500 mt-5">→</div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Target Field</label>
                      <input
                        className="input font-mono text-sm"
                        value={extraction.targetField}
                        onChange={(event) =>
                          updateCiFieldExtraction(index, { targetField: event.target.value })
                        }
                        disabled={disabled}
                        placeholder="ci.osType"
                      />
                    </div>
                    <div className="flex items-end justify-end md:justify-center">
                      <button
                        type="button"
                        className="mt-5 p-1 rounded text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                        onClick={() => removeCiFieldExtraction(index)}
                        disabled={disabled}
                        aria-label="Remove CI ResourceJson extraction"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300 hover:border-blue-400"
              onClick={addCiFieldExtraction}
              disabled={disabled}
            >
              + Add CI ResourceJson Extraction
            </button>
          </div>

          {/* From Event Payload subsection */}
          <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-200">From Event Payload</h5>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {fieldExtractionRules.length} {fieldExtractionRules.length === 1 ? 'rule' : 'rules'}
              </span>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 px-3 py-2 text-sm text-blue-800 dark:text-blue-200">
              Extracted variables are available as {'{{ci.variableName}}'} in incident title/description templates
            </div>

            <div className="space-y-3">
              {fieldExtractionRules.map((rule) => {
                const isKnownField = EXTRACTION_FIELD_OPTIONS.some((option) => option === rule.ciField);
                const showCustom = !isKnownField;
                const showAggregation = rule.sourceCiScope !== 'primary';

                return (
                  <div key={rule.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={(event) => updateExtractionRule(rule.id, { enabled: event.target.checked })}
                          disabled={disabled}
                        />
                        Enabled
                      </label>
                      <button
                        type="button"
                        className="ml-auto p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={() => removeExtractionRule(rule.id)}
                        disabled={disabled}
                        aria-label="Delete event payload extraction rule"
                      >
                        🗑
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">CI Field</label>
                        <select
                          className="input"
                          value={showCustom ? CUSTOM_FIELD_SENTINEL : rule.ciField}
                          onChange={(event) => {
                            if (event.target.value === CUSTOM_FIELD_SENTINEL) {
                              updateExtractionRule(rule.id, { ciField: '' });
                              return;
                            }

                            updateExtractionRule(rule.id, { ciField: event.target.value });
                          }}
                          disabled={disabled}
                        >
                          {EXTRACTION_FIELD_OPTIONS.map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                          ))}
                          <option value={CUSTOM_FIELD_SENTINEL}>Custom…</option>
                        </select>
                        {showCustom && (
                          <input
                            className="input mt-2"
                            value={rule.ciField}
                            onChange={(event) => updateExtractionRule(rule.id, { ciField: event.target.value })}
                            disabled={disabled}
                            placeholder="Tag:key or FieldValue:key"
                          />
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Variable Name</label>
                        <input
                          className="input"
                          value={rule.variableName}
                          onChange={(event) => updateExtractionRule(rule.id, { variableName: event.target.value })}
                          disabled={disabled}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Scope</label>
                        <select
                          className="input"
                          value={rule.sourceCiScope}
                          onChange={(event) =>
                            updateExtractionRule(rule.id, {
                              sourceCiScope: event.target.value as CiFieldSource,
                              arrayAggregation:
                                event.target.value === 'primary' ? 'first' : rule.arrayAggregation,
                            })
                          }
                          disabled={disabled}
                        >
                          {EXTRACTION_SCOPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Aggregation</label>
                        <select
                          className="input"
                          value={rule.arrayAggregation}
                          onChange={(event) =>
                            updateExtractionRule(rule.id, { arrayAggregation: event.target.value as ArrayAggregation })
                          }
                          disabled={disabled || !showAggregation}
                        >
                          {EXTRACTION_AGGREGATION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Join Separator</label>
                        {rule.arrayAggregation === 'join' ? (
                          <input
                            className="input"
                            value={rule.joinSeparator ?? ', '}
                            onChange={(event) =>
                              updateExtractionRule(rule.id, { joinSeparator: event.target.value })
                            }
                            disabled={disabled}
                          />
                        ) : (
                          <input className="input" value="" disabled placeholder="Only for Join" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300 hover:border-blue-400"
              onClick={addExtractionRule}
              disabled={disabled}
            >
              + Add Event Payload Extraction Rule
            </button>
          </div>
        </div>
      </details>

      {/* 4. Automation Lookups Section */}
      <details className="rounded-lg border border-amber-200 dark:border-amber-700 bg-white dark:bg-gray-900">
        <summary className="cursor-pointer px-5 py-4 flex items-center gap-3 select-none list-none">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">⚡ Automation Lookups</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
            {config.automationLookups?.length || 0} configured
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto mr-2">
            Execute scripts on targets to enrich alerts with additional data
          </span>
        </summary>
        <div className="px-5 pb-5 space-y-4 border-t border-amber-100 dark:border-amber-800 pt-4">
          {(config.automationLookups ?? []).map((lookup, index) => {
            const effectiveTrigger = migrateAutomationTrigger(lookup.triggerCondition);
            const selectedLookupScript =
              (lookup.scriptId ? scriptsById.get(lookup.scriptId) : undefined)
              ?? (lookup.predefinedScriptId ? scriptsBySystemKey.get(lookup.predefinedScriptId) : undefined);

            return (
              <details key={index} className="group rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 mt-4">
                <summary className="cursor-pointer px-4 py-3 flex items-center gap-3 select-none list-none hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <svg
                    className="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <input
                    type="checkbox"
                    checked={lookup.enabled}
                    onChange={(e) => {
                      e.stopPropagation();
                      const updated = [...(config.automationLookups ?? [])];
                      updated[index] = { ...updated[index], enabled: e.target.checked };
                      updateConfig({ automationLookups: updated });
                    }}
                    disabled={disabled}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                    aria-label={`Enable ${lookup.name}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <input
                    className="input flex-1 min-w-0"
                    value={lookup.name}
                    onChange={(e) => {
                      const updated = [...(config.automationLookups ?? [])];
                      updated[index] = { ...updated[index], name: e.target.value };
                      updateConfig({ automationLookups: updated });
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    disabled={disabled}
                    placeholder="Lookup name"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const updated = (config.automationLookups ?? []).filter((_, i) => i !== index);
                      updateConfig({ automationLookups: updated });
                    }}
                    disabled={disabled}
                    className="p-1 rounded text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 flex-shrink-0"
                    aria-label={`Remove ${lookup.name}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </summary>
                <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-800">

                {/* Trigger Condition */}
                <details className="border border-gray-100 dark:border-gray-800 rounded-md" open>
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                    Trigger Condition{' '}
                    {effectiveTrigger?.conditions?.length
                      ? `(${effectiveTrigger.conditions.length} conditions)`
                      : '(none — always runs)'}
                  </summary>
                  <div className="px-3 pb-3 pt-2">
                    <ConditionEditor
                      conditions={effectiveTrigger?.conditions ?? []}
                      conditionLogic={effectiveTrigger?.conditionLogic ?? 'and'}
                      onConditionsChange={(conditions) => {
                        const updated = [...(config.automationLookups ?? [])];
                        updated[index] = {
                          ...updated[index],
                          triggerCondition: {
                            conditions,
                            conditionLogic: effectiveTrigger?.conditionLogic ?? 'and',
                          },
                        };
                        updateConfig({ automationLookups: updated });
                      }}
                      onLogicChange={(logic) => {
                        const updated = [...(config.automationLookups ?? [])];
                        updated[index] = {
                          ...updated[index],
                          triggerCondition: {
                            conditions: effectiveTrigger?.conditions ?? [],
                            conditionLogic: logic,
                          },
                        };
                        updateConfig({ automationLookups: updated });
                      }}
                      fieldSuggestions={suggestions}
                    />
                  </div>
                </details>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Script
                    </label>
                    <select
                      className="input"
                      value={selectedLookupScript?.id ?? ''}
                      onChange={(e) => {
                        const nextScriptId = e.target.value;
                        const script = scriptsById.get(nextScriptId);
                        const updated = [...(config.automationLookups ?? [])];
                        updated[index] = {
                          ...updated[index],
                          scriptId: nextScriptId || undefined,
                          predefinedScriptId: script?.systemKey ?? undefined,
                          customScript: nextScriptId ? undefined : updated[index].customScript,
                          scriptParameters: nextScriptId
                            ? buildScriptParameterDefaults(script?.parameters)
                            : updated[index].scriptParameters,
                        };
                        updateConfig({ automationLookups: updated });
                      }}
                      disabled={disabled}
                    >
                      <option value="">Custom Script</option>
                      {isLoadingScripts ? (
                        <option value="" disabled>Loading scripts...</option>
                      ) : null}
                      {!isLoadingScripts && activeScripts.length === 0 ? (
                        <option value="" disabled>No scripts available</option>
                      ) : null}
                      {(Object.entries(scriptsByScope) as Array<[ScriptScope, AvailableScript[]]>).map(([scope, scripts]) =>
                        scripts.length > 0 ? (
                          <optgroup key={scope} label={SCRIPT_SCOPE_GROUP_LABELS[scope]}>
                            {scripts.map((script) => (
                              <option key={script.id} value={script.id}>
                                {script.name}
                              </option>
                            ))}
                          </optgroup>
                        ) : null,
                      )}
                    </select>
                    {scriptsError ? (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        Failed to load scripts. You can still use a custom script.
                      </p>
                    ) : null}
                    {selectedLookupScript ? (
                      <button
                        type="button"
                        onClick={() => setScriptModalScript(selectedLookupScript)}
                        className="mt-2 inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-primary-700 hover:bg-primary-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={disabled}
                        aria-label={`View script ${selectedLookupScript.name}`}
                      >
                        <span role="img" aria-label="book">📖</span>
                        View Script
                      </button>
                    ) : null}
                    {!selectedLookupScript && (lookup.scriptId || lookup.predefinedScriptId) ? (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        The selected script is no longer available. Choose another script or use a custom script.
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Execution Method
                    </label>
                    <select
                      className="input"
                      value={lookup.executionMethod}
                      onChange={(e) => {
                        const updated = [...(config.automationLookups ?? [])];
                        updated[index] = { ...updated[index], executionMethod: e.target.value };
                        updateConfig({ automationLookups: updated });
                      }}
                      disabled={disabled}
                    >
                      <option value="AzureVmRunCommand">Azure VM Run Command</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Service Connection
                  </label>
                    <select
                      className="input"
                      value={lookup.serviceConnectionId ?? ''}
                    onChange={(e) => {
                      const updated = [...(config.automationLookups ?? [])];
                      updated[index] = { ...updated[index], serviceConnectionId: e.target.value || null };
                      updateConfig({ automationLookups: updated });
                    }}
                      disabled={disabled || isSystemDefault || isLoadingServiceConnections || !effectiveCustomerId}
                    >
                    <option value="">No service connection</option>
                    {serviceConnections.map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.name}{connection.isEnabled ? '' : ' (disabled)'}
                      </option>
                    ))}
                  </select>
                    {isSystemDefault ? (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Service connections are customer-specific and must be configured per endpoint pipeline.
                      </p>
                    ) : !effectiveCustomerId ? (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Service connections are available when editing a customer pipeline.
                      </p>
                  ) : isLoadingServiceConnections ? (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Loading customer service connections…
                    </p>
                  ) : serviceConnections.length === 0 ? (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      No service connections found for this customer.
                    </p>
                  ) : !lookup.serviceConnectionId ? (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      ⚠️ No service connection linked — this lookup will be skipped during processing
                    </p>
                  ) : null}
                </div>

                {/* Script Parameters */}
                <details className="border border-gray-100 dark:border-gray-800 rounded-md">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                    Script Parameters ({Object.keys(lookup.scriptParameters ?? {}).length})
                  </summary>
                  <div className="px-3 pb-3 space-y-2 pt-2">
                    {Object.entries(lookup.scriptParameters ?? {}).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2">
                        <input
                          className="input text-sm w-1/3 font-mono"
                          value={key}
                          disabled
                          aria-label="Parameter name"
                        />
                        <FieldPathInput
                          value={value}
                          onChange={(nextValue) => {
                            const updated = [...(config.automationLookups ?? [])];
                            updated[index] = {
                              ...updated[index],
                              scriptParameters: { ...updated[index].scriptParameters, [key]: nextValue },
                            };
                            updateConfig({ automationLookups: updated });
                          }}
                          suggestions={suggestions}
                          disabled={disabled}
                          placeholder="Select or type source field path"
                          className="flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...(config.automationLookups ?? [])];
                            const params = { ...updated[index].scriptParameters };
                            delete params[key];
                            updated[index] = { ...updated[index], scriptParameters: params };
                            updateConfig({ automationLookups: updated });
                          }}
                          disabled={disabled}
                          className="text-red-500 hover:text-red-700 text-xs"
                          aria-label={`Remove parameter ${key}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}

                    {addingParameterForLookup === index ? (
                      <div className="space-y-2 rounded-md border border-dashed border-gray-300 dark:border-gray-700 p-2">
                        <FieldPathInput
                          value={newParameterName}
                          onChange={setNewParameterName}
                          suggestions={suggestions}
                          disabled={disabled}
                          placeholder="Parameter name (e.g., mountPath)"
                        />
                        <FieldPathInput
                          value={newParameterValue}
                          onChange={setNewParameterValue}
                          suggestions={suggestions}
                          disabled={disabled}
                          placeholder="Parameter value source (e.g., normalized.mountPath)"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const paramName = newParameterName.trim();
                              if (!paramName) return;
                              const updated = [...(config.automationLookups ?? [])];
                              updated[index] = {
                                ...updated[index],
                                scriptParameters: {
                                  ...updated[index].scriptParameters,
                                  [paramName]: newParameterValue.trim(),
                                },
                              };
                              updateConfig({ automationLookups: updated });
                              setNewParameterName('');
                              setNewParameterValue('');
                              setAddingParameterForLookup(null);
                            }}
                            disabled={disabled || !newParameterName.trim()}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAddingParameterForLookup(null);
                              setNewParameterName('');
                              setNewParameterValue('');
                            }}
                            disabled={disabled}
                            className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingParameterForLookup(index)}
                        disabled={disabled}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        + Add Parameter
                      </button>
                    )}
                  </div>
                </details>

                {/* Output Mapping */}
                <details className="border border-gray-100 dark:border-gray-800 rounded-md">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                    Output Mapping ({Object.keys(lookup.outputMapping ?? {}).length})
                  </summary>
                  <div className="px-3 pb-3 space-y-2 pt-2">
                    {Object.entries(lookup.outputMapping ?? {}).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2">
                        <input
                          className="input text-sm w-2/5 font-mono"
                          value={key}
                          disabled
                          aria-label="Target field"
                        />
                        <span className="text-gray-400">←</span>
                        <FieldPathInput
                          value={value}
                          onChange={(nextValue) => {
                            const updated = [...(config.automationLookups ?? [])];
                            updated[index] = {
                              ...updated[index],
                              outputMapping: { ...updated[index].outputMapping, [key]: nextValue },
                            };
                            updateConfig({ automationLookups: updated });
                          }}
                          suggestions={suggestions}
                          disabled={disabled}
                          placeholder="Select or type source expression"
                          className="flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...(config.automationLookups ?? [])];
                            const mapping = { ...updated[index].outputMapping };
                            delete mapping[key];
                            updated[index] = { ...updated[index], outputMapping: mapping };
                            updateConfig({ automationLookups: updated });
                          }}
                          disabled={disabled}
                          className="text-red-500 hover:text-red-700 text-xs"
                          aria-label={`Remove mapping ${key}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}

                    {addingMappingForLookup === index ? (
                      <div className="space-y-2 rounded-md border border-dashed border-gray-300 dark:border-gray-700 p-2">
                        <FieldPathInput
                          value={newMappingTarget}
                          onChange={setNewMappingTarget}
                          suggestions={suggestions}
                          disabled={disabled}
                          placeholder="Target field (e.g., enrichment.diskLun)"
                        />
                        <FieldPathInput
                          value={newMappingSource}
                          onChange={setNewMappingSource}
                          suggestions={suggestions}
                          disabled={disabled}
                          placeholder="Source expression"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const fieldName = newMappingTarget.trim();
                              if (!fieldName) return;
                              const updated = [...(config.automationLookups ?? [])];
                              updated[index] = {
                                ...updated[index],
                                outputMapping: {
                                  ...updated[index].outputMapping,
                                  [fieldName]: newMappingSource.trim(),
                                },
                              };
                              updateConfig({ automationLookups: updated });
                              setNewMappingTarget('');
                              setNewMappingSource('');
                              setAddingMappingForLookup(null);
                            }}
                            disabled={disabled || !newMappingTarget.trim()}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAddingMappingForLookup(null);
                              setNewMappingTarget('');
                              setNewMappingSource('');
                            }}
                            disabled={disabled}
                            className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingMappingForLookup(index)}
                        disabled={disabled}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        + Add Mapping
                      </button>
                    )}
                  </div>
                </details>

                {/* Timeout and Cache */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Timeout (seconds)</label>
                    <input
                      type="number"
                      min={5}
                      max={120}
                      className="input text-sm"
                      value={lookup.timeoutSeconds}
                      onChange={(e) => {
                        const updated = [...(config.automationLookups ?? [])];
                        updated[index] = { ...updated[index], timeoutSeconds: Number(e.target.value) || 45 };
                        updateConfig({ automationLookups: updated });
                      }}
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Cache Duration (hours)</label>
                    <input
                      type="number"
                      min={0}
                      max={168}
                      className="input text-sm"
                      value={lookup.cacheDurationHours}
                      onChange={(e) => {
                        const updated = [...(config.automationLookups ?? [])];
                        updated[index] = { ...updated[index], cacheDurationHours: Number(e.target.value) || 24 };
                        updateConfig({ automationLookups: updated });
                      }}
                      disabled={disabled}
                    />
                  </div>
                </div>

                {!selectedLookupScript && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Custom Script</label>
                    <textarea
                      className="input font-mono text-sm"
                      rows={6}
                      value={lookup.customScript ?? ''}
                      onChange={(e) => {
                        const updated = [...(config.automationLookups ?? [])];
                        updated[index] = { ...updated[index], customScript: e.target.value };
                        updateConfig({ automationLookups: updated });
                      }}
                      disabled={disabled}
                      placeholder="#!/bin/bash&#10;# Your script here..."
                    />
                  </div>
                )}
                </div>
              </details>
            );
          })}

          <button
            type="button"
            onClick={() => {
              const newLookup = {
                name: `Automation Lookup ${(config.automationLookups?.length ?? 0) + 1}`,
                enabled: false,
                executionMethod: 'AzureVmRunCommand',
                serviceConnectionId: null,
                timeoutSeconds: 45,
                cacheDurationHours: 24,
                scriptParameters: {},
                outputMapping: {},
              };
              updateConfig({ automationLookups: [...(config.automationLookups ?? []), newLookup] });
            }}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 mt-2"
          >
            + Add Automation Lookup
          </button>
        </div>
      </details>

      {/* 5. Additional Enrichment Rules */}
      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <summary className="cursor-pointer px-5 py-4 flex items-center gap-3 select-none list-none">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">🔧 Additional Enrichment Rules</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
            {config.rules.length} {config.rules.length === 1 ? 'rule' : 'rules'}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto mr-2">
            Static, Lookup, Custom
          </span>
        </summary>
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 dark:border-gray-800 pt-4">
          {config.rules.map((rule, index) => (
            <div key={rule.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3 mt-4">
              <div className="flex items-center gap-2">
                <input
                  className="input flex-1"
                  value={rule.name}
                  onChange={(event) => updateLegacyRule(rule.id, { name: event.target.value })}
                  disabled={disabled}
                  placeholder="Rule name"
                />
                <label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={(event) => updateLegacyRule(rule.id, { enabled: event.target.checked })}
                    disabled={disabled}
                  />
                  Enabled
                </label>
                <button
                  type="button"
                  className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
                  onClick={() => moveLegacyRule(index, 'up')}
                  disabled={disabled || index === 0}
                  aria-label="Move rule up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
                  onClick={() => moveLegacyRule(index, 'down')}
                  disabled={disabled || index === config.rules.length - 1}
                  aria-label="Move rule down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={() => removeLegacyRule(rule.id)}
                  disabled={disabled}
                  aria-label="Delete legacy rule"
                >
                  🗑
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Source Type</label>
                  <select
                    className="input"
                    value={rule.sourceType}
                    onChange={(event) =>
                      updateLegacyRule(rule.id, {
                        sourceType: event.target.value as EnrichmentSourceType,
                      })
                    }
                    disabled={disabled}
                  >
                    {LEGACY_SOURCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Target Field</label>
                  <input
                    className="input"
                    value={rule.targetField}
                    onChange={(event) => updateLegacyRule(rule.id, { targetField: event.target.value })}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Source Field</label>
                  <FieldPathInput
                    value={rule.sourceField ?? ''}
                    onChange={(sourceField) => updateLegacyRule(rule.id, { sourceField })}
                    suggestions={suggestions}
                    disabled={disabled}
                    placeholder="Optional"
                  />
                </div>
              </div>

              {rule.sourceType === 'static' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Static Value</label>
                  <input
                    className="input"
                    value={rule.staticValue ?? ''}
                    onChange={(event) => updateLegacyRule(rule.id, { staticValue: event.target.value })}
                    disabled={disabled}
                  />
                </div>
              )}

              {rule.sourceType === 'lookup' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Lookup Table (JSON)</label>
                  <textarea
                    className="input min-h-[80px] font-mono text-xs"
                    value={JSON.stringify(rule.lookupTable ?? {}, null, 2)}
                    onChange={(event) => {
                      try {
                        const parsed = JSON.parse(event.target.value) as Record<string, string>;
                        updateLegacyRule(rule.id, { lookupTable: parsed });
                      } catch {
                        // Keep current value if invalid JSON is entered.
                      }
                    }}
                    disabled={disabled}
                  />
                </div>
              )}

              {rule.sourceType === 'custom' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Expression</label>
                  <textarea
                    className="input min-h-[80px] font-mono text-xs"
                    value={rule.expression ?? ''}
                    onChange={(event) => updateLegacyRule(rule.id, { expression: event.target.value })}
                    disabled={disabled}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rule Timeout (ms)</label>
                <input
                  type="number"
                  min={100}
                  className="input"
                  value={rule.timeoutMs ?? config.defaultTimeoutMs}
                  onChange={(event) => updateLegacyRule(rule.id, { timeoutMs: Number(event.target.value) || 100 })}
                  disabled={disabled}
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300 hover:border-blue-400"
            onClick={addLegacyRule}
            disabled={disabled}
          >
            Add Enrichment Rule
          </button>
        </div>
      </details>
      </div>

      {/* Script Viewer Modal */}
      {scriptModalScript && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="script-viewer-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setScriptModalScript(null);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setScriptModalScript(null);
            }
          }}
        >
          <div className="w-full max-w-5xl rounded-lg bg-white shadow-2xl dark:bg-gray-800 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div>
                <h2 id="script-viewer-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {scriptModalScript.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {scriptModalScript.scriptType} • {scriptModalScript.osTarget}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setScriptModalScript(null)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close script viewer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {scriptModalScript.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {scriptModalScript.description}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Script Content
                </label>
                <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                  <Editor
                    value={scriptModalScript.scriptContent ?? 'Script content is not available.'}
                    language={scriptModalScript.scriptType === 'PowerShell' ? 'powershell' : 'shell'}
                    theme="vs-dark"
                    height="400px"
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      contextmenu: false,
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                type="button"
                onClick={() => setScriptModalScript(null)}
                className="w-full px-4 py-2.5 text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 rounded-lg transition-colors"
                aria-label="Close script viewer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
